'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { getAccount } from '@/lib/auth'
import { interpolateTemplate } from '@/lib/interpolate'

const DEBUG = String(process.env.NEXT_PUBLIC_DEBUG_AUTH || '').toLowerCase() === 'true'
const plog = (...args) => { if (DEBUG) try { console.log('[PAGE][CAMPAIGNS]', ...args) } catch {} }

export default function CampaignsPage() {
  const router = useRouter()
  const [account, setAccount] = useState(null)
  const [guarding, setGuarding] = useState(true)
  const [loading, setLoading] = useState(true)
  const [campaigns, setCampaigns] = useState([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [deleteFeedback, setDeleteFeedback] = useState({ type: null, message: '' })
  const [businessName, setBusinessName] = useState('')

  const triggerLabel = {
    new_signup: 'New Signup',
    never_ordered: 'Never Ordered',
    no_order_days: 'No Recent Orders',
    first_order: 'First Order',
    declining_activity: 'Declining Activity',
    high_value: 'High Value Customer',
    loyalty_interval: 'Loyalty Milestones',
  }

  useEffect(() => {
    let mounted = true
    plog('mount, fetching account')
    ;(async () => {
      const acc = await getAccount()
      if (!mounted) return
      plog('account response', Boolean(acc))
      if (!acc) {
        plog('redirect -> /login')
        router.replace('/login')
      }
      else {
        // Subscription guard
        try {
          const res = await fetch('/api/billing/me', { method: 'GET', credentials: 'include', cache: 'no-store' })
          if (res.ok) {
            const data = await res.json()
            const status = (data?.stripe?.status || '').toLowerCase()
            const subscribed = ['active','trialing','past_due'].includes(status)
            if (!subscribed) {
              router.replace('/subscription')
              return
            }
          } else {
            router.replace('/subscription')
            return
          }
        } catch {
          router.replace('/subscription')
          return
        }
        setAccount(acc)
        await loadCampaigns()
      }
      setLoading(false)
      setGuarding(false)
    })()
    return () => { mounted = false }
  }, [router])

  // Load CleanCloud settings for interpolation (e.g., businessName)
  useEffect(() => {
    if (!account) return
    ;(async () => {
      try {
        const res = await fetch('/api/settings/cleancloud', { method: 'GET', credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          setBusinessName(data.businessName || '')
        }
      } catch (e) {
        console.warn('Failed to load CleanCloud settings for campaigns preview:', e?.message || e)
      }
    })()
  }, [account])

  if (guarding) return null

  async function loadCampaigns() {
    setLoadingCampaigns(true)
    try {
      const res = await fetch('/api/automation/workflows')
      if (res.ok) {
        const data = await res.json()
        setCampaigns(data.automations || [])
      }
    } catch (error) {
      console.error('Error loading campaigns:', error)
    } finally {
      setLoadingCampaigns(false)
    }
  }

  const toggleCampaignStatus = async (campaignId, currentStatus) => {
    try {
      // Find the campaign to get its complete data
      const campaign = campaigns.find(c => c.$id === campaignId)
      if (!campaign) return
      
      // Parse the ruleJson to get the actual campaign data
      let parsedRule = {}
      try {
        parsedRule = JSON.parse(campaign.ruleJson || '{}')
      } catch (e) {
        console.error('Error parsing ruleJson:', e)
        return
      }
      
      // Send complete campaign data with updated enabled status
      const updatedData = {
        name: parsedRule.name || campaign.name || 'Untitled Campaign',
        trigger: parsedRule.trigger || campaign.key || 'new_signup',
        params: parsedRule.params || {},
        messages: parsedRule.messages || [],
        enabled: !currentStatus
      }
      
      console.log('Sending update data:', updatedData)
      
      const res = await fetch(`/api/automation/workflows/${campaignId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData),
      })
      
      if (res.ok) {
        setCampaigns(campaigns.map(c => 
          c.$id === campaignId ? { ...c, enabled: !currentStatus } : c
        ))
      } else {
        const errorData = await res.text()
        console.error('Failed to toggle campaign status:', res.status, errorData)
      }
    } catch (error) {
      console.error('Error toggling campaign status:', error)
    }
  }

  const deleteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return
    
    try {
      let res = await fetch(`/api/automation/workflows/${campaignId}`, {
        method: 'DELETE'
      })

      // If dynamic [id] route isn't matched for DELETE in this deploy, fall back to base route with ?id=
      if (!res.ok && (res.status === 404 || res.status === 405)) {
        res = await fetch(`/api/automation/workflows?id=${encodeURIComponent(campaignId)}`, {
          method: 'DELETE'
        })
      }

      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        setCampaigns(campaigns.filter(c => c.$id !== campaignId))
        const canceled = data?.canceledQueued ?? 0
        setDeleteFeedback({ type: 'success', message: `Campaign deleted. ${canceled} queued messages canceled.` })
      } else {
        const text = await res.text().catch(() => '')
        setDeleteFeedback({ type: 'error', message: `Failed to delete campaign (${res.status}). ${text || ''}`.trim() })
      }
    } catch (error) {
      console.error('Error deleting campaign:', error)
      setDeleteFeedback({ type: 'error', message: `Error deleting campaign: ${error?.message || String(error)}` })
    }
  }

  const getCampaignStats = (campaign) => {
    // Parse campaign data from ruleJson to get messages
    let parsedRule = {}
    try {
      parsedRule = JSON.parse(campaign.ruleJson || '{}')
    } catch (e) {
      console.error('Error parsing campaign ruleJson for stats:', e)
    }
    
    const messages = parsedRule.messages || []
    const messageCount = messages.length
    const totalDays = messages.reduce((total, msg, index) => {
      if (index === 0) return msg.day || 0
      return total + (msg.day || 0)
    }, 0)
    
    return { messageCount, totalDays }
  }

  if (loading) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <div className="mx-auto max-w-7xl">
        {deleteFeedback.type && (
          <div className={`mb-4 rounded-lg border p-3 text-sm ${
            deleteFeedback.type === 'success'
              ? 'border-emerald-800 bg-emerald-950/50 text-emerald-200'
              : 'border-red-800 bg-red-950/50 text-red-200'
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div>{deleteFeedback.message}</div>
              <button
                onClick={() => setDeleteFeedback({ type: null, message: '' })}
                className="rounded bg-slate-800/50 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700/70"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-100">Campaign Management</h1>
              <p className="mt-2 text-slate-400">Manage and monitor your SMS automation campaigns</p>
              <p className="text-sm text-slate-500">Signed in as {account?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadCampaigns}
                disabled={loadingCampaigns}
                className="rounded-lg bg-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-600 disabled:opacity-50"
              >
                {loadingCampaigns ? 'Loading...' : 'Refresh'}
              </button>
              <button
                onClick={() => router.push('/dashboard')}
                className="rounded-lg px-4 py-2 text-sm font-medium text-white hover:opacity-80" style={{backgroundColor: '#8B5CF6'}}
              >
                Create New Campaign
              </button>
            </div>
          </div>
        </div>

        {/* Campaign Statistics */}
        <div className="mb-8 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
            <div className="text-2xl font-bold text-slate-100">{campaigns.length}</div>
            <div className="text-sm text-slate-400">Total Campaigns</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
            <div className="text-2xl font-bold text-emerald-400">
              {campaigns.filter(c => c.enabled).length}
            </div>
            <div className="text-sm text-slate-400">Active Campaigns</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
            <div className="text-2xl font-bold text-slate-400">
              {campaigns.filter(c => !c.enabled).length}
            </div>
            <div className="text-sm text-slate-400">Inactive Campaigns</div>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
            <div className="text-2xl font-bold text-blue-400">
              {campaigns.reduce((total, c) => total + (c.messages?.length || 0), 0)}
            </div>
            <div className="text-sm text-slate-400">Total Messages</div>
          </div>
        </div>

        {/* Campaigns List */}
        {loadingCampaigns ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-12 text-center">
            <div className="mb-2 text-slate-300">Loading campaigns…</div>
            <div className="animate-pulse text-slate-500 text-sm">Please wait</div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-12 text-center">
            <div className="mb-4 text-6xl">📱</div>
            <h3 className="mb-2 text-xl font-semibold text-slate-100">No campaigns yet</h3>
            <p className="mb-6 text-slate-400">Create your first SMS automation campaign to get started</p>
            <button
              onClick={() => router.push('/dashboard')}
              className="rounded-lg px-6 py-3 font-medium text-white hover:opacity-80" style={{backgroundColor: '#8B5CF6'}}
            >
              Create Your First Campaign
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => {
              const stats = getCampaignStats(campaign)
              
              // Parse campaign data from ruleJson
              let parsedRule = {}
              try {
                parsedRule = JSON.parse(campaign.ruleJson || '{}')
                console.log('Campaign debug:', {
                  id: campaign.$id,
                  ruleJson: campaign.ruleJson,
                  parsedRule: parsedRule,
                  campaignDirectName: campaign.name,
                  parsedName: parsedRule.name
                })
              } catch (e) {
                console.error('Error parsing campaign ruleJson:', e)
              }
              
              const campaignTrigger = parsedRule.trigger || campaign.key || 'new_signup'
              const campaignName = triggerLabel[campaignTrigger] || 'SMS Campaign'
              
              return (
                <div key={campaign.$id} className="rounded-xl border border-slate-800 bg-slate-900/80 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-3 flex items-center gap-3">
                        <h3 className="text-xl font-semibold text-slate-100">{campaignName}</h3>
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                          campaign.enabled 
                            ? 'bg-emerald-500/15 text-emerald-300' 
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {campaign.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      
                      <div className="mb-4 grid gap-4 md:grid-cols-3">
                        <div>
                          <div className="text-sm text-slate-400">Trigger</div>
                          <div className="font-medium text-slate-200">{triggerLabel[campaignTrigger]}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Messages</div>
                          <div className="font-medium text-slate-200">{stats.messageCount} messages</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">Duration</div>
                          <div className="font-medium text-slate-200">{stats.totalDays} days</div>
                        </div>
                      </div>

                      <div className="text-sm text-slate-400">
                        Created: {new Date(campaign.$createdAt).toLocaleDateString()} • 
                        Last updated: {new Date(campaign.$updatedAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {(!campaign.enabled) && (
                        <button
                          onClick={() => toggleCampaignStatus(campaign.$id, campaign.enabled)}
                          className={"rounded-lg px-3 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"}
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => router.push(`/dashboard?edit=${campaign.$id}`)}
                        className="rounded-lg px-3 py-2 text-sm font-medium text-white hover:opacity-80" style={{backgroundColor: '#8B5CF6'}}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteCampaign(campaign.$id)}
                        className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Message Preview */}
                  {parsedRule.messages && parsedRule.messages.length > 0 && (
                    <div className="mt-4 border-t border-slate-700 pt-4">
                      <div className="mb-2 text-sm font-medium text-slate-300">Message Sequence Preview</div>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {parsedRule.messages.slice(0, 3).map((message, index) => (
                          <div key={message.id ?? `${index}-${message.day}-${(message.message||'').slice(0,10)}`}
                               className="min-w-[200px] rounded-lg border border-slate-700 bg-slate-950 p-3">
                            <div className="mb-1 flex items-center gap-2">
                              <span className="flex h-4 w-4 items-center justify-center rounded-full text-xs text-white" style={{backgroundColor: '#8B5CF6'}}>
                                {index + 1}
                              </span>
                              <span className="text-xs text-slate-400">
                                Day {message.day}
                              </span>
                            </div>
                            <p className="text-xs text-slate-200 line-clamp-3">
                              {interpolateTemplate(
                                message.message || '',
                                {
                                  customerName: 'Alex Doe',
                                  businessName,
                                                                    orderDateFormatted: '08/20/2025',
                                }
                              )}
                            </p>
                          </div>
                        ))}
                        {parsedRule.messages.length > 3 && (
                          <div className="flex min-w-[100px] items-center justify-center rounded-lg border border-slate-700 bg-slate-950 p-3">
                            <span className="text-xs text-slate-400">
                              +{parsedRule.messages.length - 3} more
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
